import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import {
  Box, Typography, CircularProgress, Alert,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  Chip, Tooltip, IconButton, TextField, Button,
  Dialog, DialogTitle, DialogContent, DialogActions,
  Snackbar, FormControlLabel, Checkbox,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import CommentIcon from "@mui/icons-material/Comment";
import ListAltIcon from "@mui/icons-material/ListAlt";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import WarningAmberOutlinedIcon from "@mui/icons-material/WarningAmberOutlined";
import CloseIcon from "@mui/icons-material/Close";
import RefreshIcon from "@mui/icons-material/Refresh";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import AttachEmailIcon from "@mui/icons-material/AttachEmail";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import PeopleOutlinedIcon from "@mui/icons-material/PeopleOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import { useNavigate } from "react-router-dom";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";
import ZephyrIconWhite from "@/Images/Icons/Zephyr_Icon_White.png";
import WriteOffButton from "@/components/zephyr/WriteOffButton";
import ZephyrEmailButtons from "@/components/zephyr/ZephyrEmailButtons";
import ZephyrComposeModal from "@/components/zephyr/ZephyrComposeModal";

const Z_PURPLE = "#38184f";
const Z_BLUE = "#2baede";
const fmt = (v) => new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(Number(v ?? 0));
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-AU") : "—";
const toDateInput = (d) => d.toISOString().slice(0, 10);
const addDaysToToday = (n) => { const d = new Date(); d.setDate(d.getDate() + n); return toDateInput(d); };

// ── Invoice Comment Modal (full version with recheck date) ────────────────────
function InvoiceCommentModal({ open, invoiceID, claimID, currentComment, onClose, onSaved, otherInvoiceIDs = [] }) {
  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const [recheckDate, setRecheckDate] = useState(() => addDaysToToday(7));
  const [xDays, setXDays] = useState(14);
  const [applyToAll, setApplyToAll] = useState(false);

  useEffect(() => {
    if (open) {
      setValue(currentComment ?? "");
      setRecheckDate(addDaysToToday(7));
      setXDays(14);
      setApplyToAll(false);
    }
  }, [open, currentComment]);

  const save = async () => {
    if (!value.trim()) return;
    setSaving(true);
    await phappsRequest(
      `${API_PATHS.ZEPHYR_ADMIN_UPDATE_INVOICE_COMMENT}/${invoiceID}`,
      "POST",
      { comment: value.trim(), recheckDate }
    ).catch(() => {});
    await phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_ADD_CLAIM_COMMENT}/${claimID}`, "POST", {
      comment: `${value.trim()} (Invoice #${invoiceID})`,
    }).catch(() => {});
    if (applyToAll) {
      for (const otherId of otherInvoiceIDs) {
        await phappsRequest(
          `${API_PATHS.ZEPHYR_ADMIN_UPDATE_INVOICE_COMMENT}/${otherId}`,
          "POST",
          { comment: value.trim(), recheckDate }
        ).catch(() => {});
      }
    }
    setSaving(false);
    onSaved({ invoiceID, comment: value.trim(), recheckDate, appliedInvoiceIDs: applyToAll ? otherInvoiceIDs : [] });
    onClose();
  };

  const chipBtn = (label, fn) => (
    <Button key={label} size="small" variant="outlined" onClick={fn}
      sx={{ textTransform: "none", fontSize: "0.75rem", py: 0.3, px: 1, minWidth: 0,
        borderColor: "#cbd5e1", color: "#475569" }}>
      {label}
    </Button>
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      <DialogTitle sx={{
        background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`, color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5,
      }}>
        <span>Invoice #{invoiceID} — Comment</span>
        <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: "24px !important", display: "flex", flexDirection: "column", gap: 2 }}>
        <TextField multiline rows={3} fullWidth size="small" autoFocus
          placeholder="Enter invoice comment…"
          value={value} onChange={(e) => setValue(e.target.value)} />
        <Typography variant="caption" color="text.secondary">
          Also logged to claim comments as: &ldquo;{value.trim() || "…"} (Invoice #{invoiceID})&rdquo;
        </Typography>
        <Box>
          <Typography variant="caption" fontWeight={700} color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
            Recheck Date
          </Typography>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center", mb: 1 }}>
            {chipBtn("1 Week", () => setRecheckDate(addDaysToToday(7)))}
            {chipBtn("1 Month", () => setRecheckDate(addDaysToToday(30)))}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <input
                type="number" min={1} max={365} value={xDays}
                onChange={(e) => setXDays(Math.max(1, Number(e.target.value)))}
                style={{ width: 52, padding: "3px 6px", border: "1px solid #cbd5e1",
                  borderRadius: 4, fontSize: "0.8rem" }}
              />
              {chipBtn("Days", () => setRecheckDate(addDaysToToday(xDays)))}
            </Box>
          </Box>
          <TextField
            type="date" size="small" fullWidth
            value={recheckDate}
            onChange={(e) => setRecheckDate(e.target.value)}
            inputProps={{ min: toDateInput(new Date()) }}
          />
        </Box>
        {otherInvoiceIDs.length > 0 && (
          <FormControlLabel
            control={
              <Checkbox
                checked={applyToAll}
                onChange={e => setApplyToAll(e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography variant="body2">
                Apply this comment &amp; recheck date to all other outstanding invoices ({otherInvoiceIDs.length})
              </Typography>
            }
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving || !value.trim()}
          sx={{ textTransform: "none", fontWeight: 700,
            background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})` }}>
          {saving ? "Saving…" : "Save & Log"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Full Script Report Modal ──────────────────────────────────────────────────
function FullScriptReportModal({ open, claimID, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open || !claimID) return;
    setLoading(true);
    setData(null);
    phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_FULL_SCRIPT_REPORT}/${claimID}`, "GET")
      .then((res) => setData(res?.data ?? null))
      .finally(() => setLoading(false));
  }, [open, claimID]);

  const c = data?.claim;
  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      <DialogTitle sx={{
        background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`, color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5,
      }}>
        <span>Script Report — {c?.FullName ?? `Claim #${claimID}`}</span>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {claimID && (
            <Tooltip title="Go to Customer">
              <IconButton size="small" sx={{ color: "#fff" }}
                onClick={() => { onClose(); navigate("/zephyr-admin/customers", { state: { claimID } }); }}>
                <OpenInNewOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}><CloseIcon /></IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {c?.FullName && (
          <Box sx={{ mb: 1.5, pb: 1.5, borderBottom: "1px solid #e2e8f0" }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}>Customer</Typography>
            <Typography fontWeight={700} sx={{ color: Z_PURPLE, fontSize: "1rem" }}>{c.FullName}</Typography>
          </Box>
        )}
        {loading
          ? <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}><CircularProgress sx={{ color: Z_PURPLE }} /></Box>
          : (
            <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 1.5 }}>
              <Table size="small">
                <TableHead sx={{ backgroundColor: `${Z_PURPLE}08` }}>
                  <TableRow>
                    {["Script ID", "Dispensed", "Item", "Qty", "Invoiced", "Received", "Status"].map(h => (
                      <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE, py: 1 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(data?.scripts ?? []).map((s) => (
                    <TableRow key={s.ScriptID} sx={{ "& td": { py: 0.6, px: 1, fontSize: "0.8rem" } }}>
                      <TableCell>{s.ScriptID}</TableCell>
                      <TableCell>{fmtDate(s.DateDispensed)}</TableCell>
                      <TableCell>{s.txtItem}</TableCell>
                      <TableCell align="center">{s.Qty ?? "—"}</TableCell>
                      <TableCell align="right">{fmt(s.InvAmount)}</TableCell>
                      <TableCell align="right">{s.RcvdAmount != null ? fmt(s.RcvdAmount) : "—"}</TableCell>
                      <TableCell>
                        {s.PaymentID ? <Chip label="Paid" size="small" sx={{ background: "#dcfce7", color: "#16a34a", fontSize: "0.72rem" }} /> :
                         Number(s.WrittenOff) ? <Chip label="Written Off" size="small" color="error" sx={{ fontSize: "0.72rem" }} /> :
                         Number(s.OnHold) === -1 ? <Chip label="Missed" size="small" color="warning" sx={{ fontSize: "0.72rem" }} /> :
                         null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Invoice Detail Modal ──────────────────────────────────────────────────────
function InvoiceDetailModal({ open, invoiceID, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !invoiceID) return;
    setLoading(true);
    setData(null);
    phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_INVOICE_DETAILS}/${invoiceID}`, "GET")
      .then((res) => setData(res?.data ?? null))
      .finally(() => setLoading(false));
  }, [open, invoiceID]);

  const scripts = data?.scripts ?? [];
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}>
      <DialogTitle sx={{
        background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`, color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "space-between", py: 1.5,
      }}>
        <span>Invoice {invoiceID}</span>
        <IconButton onClick={onClose} size="small" sx={{ color: "#fff" }}><CloseIcon /></IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", pt: 2 }}>
            <CircularProgress sx={{ color: Z_PURPLE }} />
          </Box>
        ) : (
          <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 1 }}>
            <Table size="small">
              <TableHead sx={{ backgroundColor: `${Z_PURPLE}08` }}>
                <TableRow>
                  {["Dispensed", "Prescribed", "Item", "Qty", "Doctor", "Pharm Amt", "Inv Amt"].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE, py: 1 }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {scripts.map((s) => (
                  <TableRow key={s.ScriptID} sx={{ "& td": { py: 0.6, px: 1, fontSize: "0.8rem" } }}>
                    <TableCell>{fmtDate(s.DateDispensed)}</TableCell>
                    <TableCell>{fmtDate(s.DatePrescribed)}</TableCell>
                    <TableCell>{s.txtItem}</TableCell>
                    <TableCell align="center">{s.Qty ?? "—"}</TableCell>
                    <TableCell>{s.PrescriberName || "—"}</TableCell>
                    <TableCell align="right">{s.PharmAmount != null ? fmt(s.PharmAmount) : "—"}</TableCell>
                    <TableCell align="right">{fmt(s.InvAmount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Invoice Row (fetches scripts lazily on first expand — no useEffect) ───────
function InvoiceRow({ invoice, claimID, onComment, onWriteOff, showUnseen, onOpenCompose, onEmailSuccess, onEmailError, insurerEmail, onCopyComment, hasCopySource }) {
  const [expanded, setExpanded] = useState(false);
  const [scriptsData, setScriptsData] = useState(null);
  const [scriptsLoading, setScriptsLoading] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const fetchedRef = useRef(false);
  const isUnseen = showUnseen && !invoice.invComment;

  const fetchScripts = () => {
    setScriptsLoading(true);
    phappsRequest(`${API_PATHS.ZEPHYR_ADMIN_INVOICE_DETAILS}/${invoice.invoiceID}`, "GET")
      .then((res) => setScriptsData(res?.data ?? null))
      .finally(() => setScriptsLoading(false));
  };

  const handleExpand = () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !fetchedRef.current) {
      fetchedRef.current = true;
      fetchScripts();
    }
  };

  const handleWriteOff = () => {
    if (expanded) fetchScripts();
    onWriteOff?.();
  };

  return (
    <>
      <TableRow sx={{
        "& td": { py: 0.6, px: 1.5, fontSize: "0.8rem", backgroundColor: isUnseen ? "#fffbeb" : "#fafafa" },
        outline: isUnseen ? "1px solid #fcd34d" : "none",
      }}>
        <TableCell sx={{ width: "1px", whiteSpace: "nowrap", pl: 4 }}>
          <IconButton size="small" onClick={handleExpand} sx={{ p: 0.5 }}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell sx={{ width: "1px", whiteSpace: "nowrap" }}>
          <button onClick={() => setDetailOpen(true)}
            style={{ background: "none", border: "none", cursor: "pointer", padding: 0,
              color: Z_PURPLE, fontWeight: 700, textDecoration: "underline", fontSize: "inherit" }}>
            {invoice.invoiceID}
          </button>
        </TableCell>
        <TableCell sx={{ width: "1px", whiteSpace: "nowrap" }}>{fmtDate(invoice.invoiceDate)}</TableCell>
        <TableCell align="right" sx={{ width: "1px", whiteSpace: "nowrap", color: "#dc2626", fontWeight: 600 }}>
          {fmt(invoice.outstandingAmount)}
        </TableCell>
        <TableCell align="center" sx={{ width: "1px", whiteSpace: "nowrap" }}>{invoice.scriptCount}</TableCell>
        <TableCell sx={{ width: "1px", whiteSpace: "nowrap", color: invoice.recheckDate ? "#92400e" : "#94a3b8" }}>
          {invoice.recheckDate ? fmtDate(invoice.recheckDate) : "—"}
        </TableCell>
        <TableCell sx={{ width: "1px", whiteSpace: "nowrap" }}>
          <Tooltip title="Edit comment">
            <IconButton size="small" onClick={() => onComment(invoice)} sx={{ p: 0.5, color: Z_BLUE }}>
              <CommentIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
        <TableCell>
          {invoice.invComment
            ? <Typography variant="body2" sx={{ fontSize: "0.8rem", color: "text.primary" }}>{invoice.invComment}</Typography>
            : <Typography variant="body2" sx={{ fontSize: "0.8rem", color: "#94a3b8", fontStyle: "italic" }}>No comment</Typography>}
        </TableCell>
        <TableCell sx={{ width: "1px", whiteSpace: "nowrap" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.25 }}>
            <Tooltip title="View invoice items">
              <IconButton size="small" onClick={() => setDetailOpen(true)} sx={{ p: 0.5, color: Z_PURPLE }}>
                <ReceiptLongOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {hasCopySource && (
              <Tooltip title="Copy latest comment & recheck date">
                <IconButton size="small" onClick={() => onCopyComment?.(invoice.invoiceID)} sx={{ p: 0.5, color: "#64748b" }}>
                  <ContentCopyIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <ZephyrEmailButtons
              invoiceID={invoice.invoiceID}
              insurerEmail={insurerEmail}
              onOpenCompose={onOpenCompose ? () => onOpenCompose(invoice.invoiceID) : undefined}
              onSuccess={onEmailSuccess}
              onError={onEmailError}
            />
            <WriteOffButton
              claimID={claimID}
              invoiceID={invoice.invoiceID}
              displayCount={invoice.scriptCount}
              displayTotal={invoice.outstandingAmount}
              onSuccess={handleWriteOff}
            />
          </Box>
        </TableCell>
      </TableRow>
      <InvoiceDetailModal
        open={detailOpen}
        invoiceID={invoice.invoiceID}
        onClose={() => setDetailOpen(false)}
      />

      {expanded && (
        scriptsLoading ? (
          <TableRow>
            <TableCell colSpan={9} sx={{ py: 1.5, pl: 6, backgroundColor: "#fafafa" }}>
              <CircularProgress size={18} sx={{ color: Z_PURPLE }} />
            </TableCell>
          </TableRow>
        ) : (
          <TableRow sx={{ backgroundColor: "#f8fafc" }}>
            <TableCell colSpan={9} sx={{ py: 1, px: 0 }}>
              <Box sx={{ mx: 3, mb: 1 }}>
                <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 1 }}>
                  <Table size="small">
                    <TableHead sx={{ backgroundColor: `${Z_PURPLE}08` }}>
                      <TableRow>
                        {["Script ID", "Dispensed", "Item", "Qty", "Invoiced", "Status", ""].map(h => (
                          <TableCell key={h} sx={{ fontWeight: 700, fontSize: "0.74rem", color: Z_PURPLE, py: 0.75, px: 1 }}>{h}</TableCell>
                        ))}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(scriptsData?.scripts ?? []).map((s) => (
                        <TableRow key={s.ScriptID} sx={{ "& td": { py: 0.5, px: 1, fontSize: "0.78rem" } }}>
                          <TableCell>{s.ScriptID}</TableCell>
                          <TableCell>{fmtDate(s.DateDispensed)}</TableCell>
                          <TableCell>{s.txtItem}</TableCell>
                          <TableCell align="center">{s.Qty ?? "—"}</TableCell>
                          <TableCell align="right">{fmt(s.InvAmount)}</TableCell>
                          <TableCell>
                            {s.PaymentID
                              ? <Chip label="Paid" size="small" sx={{ background: "#dcfce7", color: "#16a34a", fontSize: "0.7rem" }} />
                              : Number(s.WrittenOff)
                                ? <Chip label="Written Off" size="small" sx={{ background: "#fee2e2", color: "#dc2626", fontSize: "0.7rem" }} />
                                : Number(s.OnHold) === -1
                                  ? <Chip label="Missed" size="small" sx={{ background: "#fef9c3", color: "#92400e", fontSize: "0.7rem" }} />
                                  : <Chip label="Outstanding" size="small" sx={{ background: "#fee2e2", color: "#dc2626", fontSize: "0.7rem" }} />}
                          </TableCell>
                          <TableCell sx={{ px: "2px !important" }}>
                            <WriteOffButton
                              claimID={claimID}
                              scriptIDs={[s.ScriptID]}
                              scripts={[s]}
                              onSuccess={handleWriteOff}
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            </TableCell>
          </TableRow>
        )
      )}
    </>
  );
}

// ── Customer Row ──────────────────────────────────────────────────────────────
function CustomerRow({ customer, onFullReport, onCommentSaved, onRefresh, showUnseen, onOpenCompose, onEmailSuccess, onEmailError, insCompEmail }) {
  const [expanded, setExpanded] = useState(false);
  const [commentTarget, setCommentTarget] = useState(null);
  const navigate = useNavigate();

  const hasUnseen = customer.invoices.some(inv => !inv.invComment);

  const handleCommentSaved = useCallback(({ invoiceID, comment, recheckDate, appliedInvoiceIDs }) => {
    onCommentSaved(customer.claimID, invoiceID, comment, recheckDate);
    for (const otherId of (appliedInvoiceIDs ?? [])) {
      onCommentSaved(customer.claimID, otherId, comment, recheckDate);
    }
  }, [customer.claimID, onCommentSaved]);

  // Find the invoice with the latest recheckDate that has a comment — used as copy source
  const copySource = useMemo(() => {
    return customer.invoices
      .filter(inv => inv.recheckDate && inv.invComment)
      .sort((a, b) => new Date(b.recheckDate) - new Date(a.recheckDate))[0] ?? null;
  }, [customer.invoices]);

  const handleCopyComment = useCallback(async (targetInvoiceID) => {
    if (!copySource) return;
    await phappsRequest(
      `${API_PATHS.ZEPHYR_ADMIN_UPDATE_INVOICE_COMMENT}/${targetInvoiceID}`,
      "POST",
      { comment: copySource.invComment, recheckDate: copySource.recheckDate.slice(0, 10) }
    ).catch(() => {});
    onCommentSaved(customer.claimID, targetInvoiceID, copySource.invComment, copySource.recheckDate);
  }, [copySource, customer.claimID, onCommentSaved]);

  return (
    <>
      <TableRow sx={{
        "& td": { py: 0.8, px: 1.5, fontSize: "0.82rem" },
        borderLeft: showUnseen && hasUnseen ? `3px solid #f59e0b` : "3px solid transparent",
      }}>
        <TableCell sx={{ width: "1px" }}>
          <IconButton size="small" onClick={() => setExpanded(e => !e)} sx={{ p: 0.5 }}>
            {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </IconButton>
        </TableCell>
        <TableCell>
          <Typography sx={{ fontWeight: 600, fontSize: "0.82rem" }}>{customer.fullName}</Typography>
        </TableCell>
        <TableCell sx={{ width: "1px", whiteSpace: "nowrap" }}>{customer.claimNumber}</TableCell>
        <TableCell sx={{ width: "1px", whiteSpace: "nowrap" }}>{fmtDate(customer.dob)}</TableCell>
        <TableCell sx={{ color: "#64748b", fontSize: "0.78rem" }}>{customer.address || "—"}</TableCell>
        <TableCell sx={{ width: "1px", whiteSpace: "nowrap", fontSize: "0.78rem", color: "#374151" }}>{customer.pharmacyName || "—"}</TableCell>
        <TableCell align="right" sx={{ width: "1px", whiteSpace: "nowrap", fontWeight: 700, color: "#dc2626" }}>
          {fmt(customer.totalOutstanding)}
        </TableCell>
        <TableCell align="center" sx={{ width: "1px", whiteSpace: "nowrap", color: "#64748b" }}>
          {customer.invoices.length}
        </TableCell>
        <TableCell sx={{ width: "1px", whiteSpace: "nowrap" }}>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <Tooltip title="Full Script Report">
              <IconButton size="small" onClick={() => onFullReport(customer.claimID)} sx={{ p: 0.5, color: Z_PURPLE }}>
                <ListAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            {onOpenCompose && (
              <Tooltip title="Compose email">
                <IconButton size="small"
                  onClick={() => onOpenCompose(null, customer.claimID, insCompEmail, customer.fullName, customer.claimNumber)}
                  sx={{ p: 0.5, color: "#7b1fa2" }}>
                  <AttachEmailIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip title="Go to Customer">
              <IconButton size="small"
                onClick={() => navigate("/zephyr-admin/customers", { state: { claimID: customer.claimID } })}
                sx={{ p: 0.5, color: Z_BLUE }}>
                <PersonSearchIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </TableCell>
      </TableRow>

      {expanded && (
        <TableRow>
          <TableCell colSpan={10} sx={{ py: 0, px: 0 }}>
            <Box sx={{ mx: 2, mb: 1 }}>
              <TableContainer sx={{ border: "1px solid #e2e8f0", borderRadius: 1 }}>
                <Table size="small">
                  <TableHead sx={{ backgroundColor: `${Z_PURPLE}06` }}>
                    <TableRow>
                      <TableCell sx={{ width: "1px", py: 0.75, px: 1.5 }} />
                      {[
                        { label: "Invoice",     sx: { width: "1px", whiteSpace: "nowrap" } },
                        { label: "Date",        sx: { width: "1px", whiteSpace: "nowrap" } },
                        { label: "Outstanding", sx: { width: "1px", whiteSpace: "nowrap" }, align: "right" },
                        { label: "Scripts",     sx: { width: "1px", whiteSpace: "nowrap" }, align: "center" },
                        { label: "Recheck",     sx: { width: "1px", whiteSpace: "nowrap" } },
                        { label: "",            sx: { width: "1px", whiteSpace: "nowrap" } },
                        { label: "Comment",     sx: { width: "100%" } },
                        { label: "Options",     sx: { width: "1px", whiteSpace: "nowrap" } },
                      ].map(({ label, sx: colSx, align }, i) => (
                        <TableCell key={i} align={align ?? "left"}
                          sx={{ fontWeight: 700, fontSize: "0.76rem", color: Z_PURPLE, py: 0.75, px: 1.5, ...colSx }}>
                          {label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {customer.invoices.map(inv => (
                      <InvoiceRow
                        key={inv.invoiceID}
                        invoice={inv}
                        claimID={customer.claimID}
                        onComment={setCommentTarget}
                        onWriteOff={onRefresh}
                        showUnseen={showUnseen}
                        onOpenCompose={onOpenCompose ? (invoiceID) => onOpenCompose(invoiceID, customer.claimID, insCompEmail, customer.fullName, customer.claimNumber) : undefined}
                        onEmailSuccess={onEmailSuccess}
                        onEmailError={onEmailError}
                        insurerEmail={insCompEmail}
                        onCopyComment={handleCopyComment}
                        hasCopySource={!!copySource && copySource.recheckDate?.slice(0, 10) !== inv.recheckDate?.slice(0, 10)}
                      />
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </TableCell>
        </TableRow>
      )}

      <InvoiceCommentModal
        open={!!commentTarget}
        invoiceID={commentTarget?.invoiceID}
        claimID={customer.claimID}
        currentComment={commentTarget?.invComment}
        onClose={() => setCommentTarget(null)}
        onSaved={handleCommentSaved}
        otherInvoiceIDs={customer.invoices
          .filter(inv => inv.invoiceID !== commentTarget?.invoiceID)
          .map(inv => inv.invoiceID)}
      />
    </>
  );
}

// ── Stats Card ────────────────────────────────────────────────────────────────
function StatCard({ icon: Icon, label, value, color }) {
  return (
    <Box sx={{
      flex: 1, minWidth: 140,
      border: "1px solid #e2e8f0", borderRadius: 2, p: 2, background: "#fff",
      display: "flex", flexDirection: "column", gap: 0.5,
    }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
        <Icon sx={{ fontSize: 16, color: color ?? "#64748b" }} />
        <Typography variant="caption" color="text.secondary" fontWeight={700}>{label}</Typography>
      </Box>
      <Typography fontWeight={800} sx={{ fontSize: "1.4rem", color: color ?? Z_PURPLE, lineHeight: 1.2 }}>
        {value}
      </Typography>
    </Box>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function ZephyrOverdueInvoicesPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [scriptReport, setScriptReport] = useState(null);
  const [showUnseen, setShowUnseen] = useState(false);
  const [toast, setToast] = useState(null);
  const [composeModal, setComposeModal] = useState(null); // { invoiceID?, claimID? }

  const showToast = (msg, severity = "success") => setToast({ msg, severity });
  const hideToast = () => setToast(null);
  const openCompose = (invoiceID, claimID, insCompEmail, customerName, claimNumber) =>
    setComposeModal({ invoiceID, claimID, insCompEmail, customerName, claimNumber });

  const loadData = useCallback(() => {
    setLoading(true);
    setError(null);
    phappsRequest(API_PATHS.ZEPHYR_ADMIN_OVERDUE_INVOICES, "GET")
      .then((res) => setData(res?.data ?? null))
      .catch(() => setError("Failed to load overdue invoices."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleCommentSaved = useCallback((claimID, invoiceID, comment, recheckDate) => {
    setData(prev => {
      if (!prev) return prev;
      return {
        ...prev,
        groups: prev.groups.map(g => ({
          ...g,
          customers: g.customers.map(c =>
            c.claimID !== claimID ? c : {
              ...c,
              invoices: c.invoices.map(inv =>
                inv.invoiceID !== invoiceID ? inv : { ...inv, invComment: comment, recheckDate }
              ),
            }
          ),
        })),
      };
    });
  }, []);

  const stats = useMemo(() => {
    if (!data) return null;
    const totalCustomers = data.groups.reduce((s, g) => s + g.customerCount, 0);
    const totalOutstanding = data.groups.reduce(
      (s, g) => s + g.customers.reduce((cs, c) => cs + c.totalOutstanding, 0), 0
    );
    const totalScripts = data.groups.reduce((s, g) => s + g.scriptCount, 0);
    const totalInvoices = data.groups.reduce((s, g) => s + g.invoiceCount, 0);
    return { totalCustomers, totalOutstanding, totalScripts, totalInvoices };
  }, [data]);

  const groupHasUnseen = (g) => g.customers.some(c => c.invoices.some(inv => !inv.invComment));

  const selectedGroup = data?.groups?.[selectedIndex] ?? null;

  return (
    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2, height: "100%", minHeight: 0, overflow: "hidden" }}>

      {/* Header row */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
        <Box sx={{
          width: 36, height: 36, borderRadius: 1.5,
          background: `linear-gradient(135deg, ${Z_PURPLE}, ${Z_BLUE})`,
          display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
        }}>
          <img src={ZephyrIconWhite} alt="" style={{ height: 20, objectFit: "contain" }} />
        </Box>
        <Typography fontWeight={800} fontSize="1.25rem" sx={{ color: Z_PURPLE }}>Overdue Invoices</Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title="Refresh data">
          <IconButton onClick={loadData} disabled={loading} sx={{ color: Z_PURPLE }}>
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexShrink: 0 }}>
          <CircularProgress size={22} sx={{ color: Z_PURPLE }} />
          <Typography color="text.secondary" variant="body2">Loading…</Typography>
        </Box>
      )}
      {error && <Alert severity="error" sx={{ flexShrink: 0 }}>{error}</Alert>}

      {data && (
        <>
          {/* Stats cards */}
          {stats && (
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", flexShrink: 0 }}>
              <StatCard icon={PeopleOutlinedIcon}       label="Total Customers"  value={stats.totalCustomers} />
              <StatCard icon={AttachMoneyIcon}          label="Total Outstanding" value={fmt(stats.totalOutstanding)} color="#dc2626" />
              <StatCard icon={ReceiptLongOutlinedIcon}  label="Total Invoices"   value={stats.totalInvoices} />
              <StatCard icon={MedicationOutlinedIcon}   label="Total Scripts"    value={stats.totalScripts} />
            </Box>
          )}

          {/* Unseen alert */}
          {data.uncheckedCount > 0 && (
            <Alert
              severity="warning"
              icon={<WarningAmberOutlinedIcon />}
              sx={{
                borderRadius: 2, flexShrink: 0, cursor: "pointer",
                border: showUnseen ? "2px solid #f59e0b" : "1px solid transparent",
                "&:hover": { background: "#fef3c7" },
              }}
              onClick={() => setShowUnseen(s => !s)}
            >
              <strong>{data.uncheckedCount}</strong> unseen overdue invoice{data.uncheckedCount !== 1 ? "s" : ""} —
              {showUnseen ? " click to hide highlights" : " click to highlight them"}
            </Alert>
          )}

          {data.groups.length === 0 ? (
            <Alert severity="success">No overdue invoices due for follow-up.</Alert>
          ) : (
            <Box sx={{ display: "flex", gap: 2, flex: 1, minHeight: 0, overflow: "hidden" }}>

              {/* Left: insurer list */}
              <Box sx={{
                width: 240, flexShrink: 0,
                border: "1px solid #e2e8f0", borderRadius: 1.5, background: "#fff",
                overflowY: "auto",
              }}>
                {data.groups.map((g, i) => {
                  const hasUnseen = showUnseen && groupHasUnseen(g);
                  const groupOutstanding = g.customers.reduce((s, c) => s + c.totalOutstanding, 0);
                  return (
                    <Box
                      key={g.insCompID ?? "none"}
                      onClick={() => setSelectedIndex(i)}
                      sx={{
                        px: 2, py: 1.5,
                        cursor: "pointer",
                        borderBottom: "1px solid #f1f5f9",
                        background: selectedIndex === i ? `${Z_PURPLE}0d` : "transparent",
                        borderLeft: hasUnseen
                          ? "3px solid #f59e0b"
                          : selectedIndex === i
                            ? `3px solid ${Z_PURPLE}`
                            : "3px solid transparent",
                        "&:hover": { background: `${Z_PURPLE}08` },
                        transition: "all 0.12s",
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <Typography variant="body2" fontWeight={selectedIndex === i ? 700 : 600}
                          sx={{ color: selectedIndex === i ? Z_PURPLE : "text.primary", lineHeight: 1.3, flex: 1 }}>
                          {g.insCompName}
                        </Typography>
                        {hasUnseen && (
                          <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: "#f59e0b", flexShrink: 0 }} />
                        )}
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {g.customerCount} customer{g.customerCount !== 1 ? "s" : ""}
                      </Typography>
                      <Typography variant="caption" sx={{ color: "#dc2626", fontWeight: 600 }}>
                        {fmt(groupOutstanding)}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>

              {/* Right: customer list */}
              {selectedGroup && (
                <Box sx={{ flex: 1, minWidth: 0, display: "flex", flexDirection: "column", gap: 1.5, overflow: "hidden" }}>

                  {/* Insurer stats bar */}
                  <Box sx={{
                    display: "flex", alignItems: "center", gap: 3, flexWrap: "wrap", flexShrink: 0,
                    background: `${Z_PURPLE}08`, borderRadius: 1.5, px: 2, py: 1.2,
                    border: `1px solid ${Z_PURPLE}18`,
                  }}>
                    <Typography variant="body2" fontWeight={700} sx={{ color: Z_PURPLE }}>{selectedGroup.insCompName}</Typography>
                    {selectedGroup.insCompPhone && (
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                        <PhoneOutlinedIcon sx={{ fontSize: 15, color: Z_PURPLE }} />
                        <Typography variant="body2" sx={{ color: Z_PURPLE }}>{selectedGroup.insCompPhone}</Typography>
                      </Box>
                    )}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <PeopleOutlinedIcon sx={{ fontSize: 15, color: "#64748b" }} />
                      <Typography variant="body2" color="text.secondary">{selectedGroup.customerCount} customers</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <ReceiptLongOutlinedIcon sx={{ fontSize: 15, color: "#64748b" }} />
                      <Typography variant="body2" color="text.secondary">{selectedGroup.invoiceCount} invoices</Typography>
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      <MedicationOutlinedIcon sx={{ fontSize: 15, color: "#64748b" }} />
                      <Typography variant="body2" color="text.secondary">{selectedGroup.scriptCount} scripts</Typography>
                    </Box>
                  </Box>

                  {/* Customer table */}
                  <TableContainer sx={{
                    border: "1px solid #e2e8f0", borderRadius: 1.5, background: "#fff",
                    flex: 1, overflowY: "auto",
                  }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: "1px", py: 1, background: `${Z_PURPLE}08` }} />
                          {[
                            { label: "Name" },
                            { label: "Claim #",     sx: { width: "1px", whiteSpace: "nowrap" } },
                            { label: "DOB",          sx: { width: "1px", whiteSpace: "nowrap" } },
                            { label: "Address" },
                            { label: "Pharmacy",    sx: { width: "1px", whiteSpace: "nowrap" } },
                            { label: "Outstanding", sx: { width: "1px", whiteSpace: "nowrap" }, align: "right" },
                            { label: "Invoices",    sx: { width: "1px", whiteSpace: "nowrap" }, align: "center" },
                            { label: "",             sx: { width: "1px", whiteSpace: "nowrap" } },
                          ].map(({ label, sx: colSx, align }, i) => (
                            <TableCell key={i} align={align ?? "left"}
                              sx={{ fontWeight: 700, fontSize: "0.78rem", color: Z_PURPLE, py: 1,
                                background: `${Z_PURPLE}08`, ...colSx }}>
                              {label}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedGroup.customers.map((cust) => (
                          <CustomerRow
                            key={cust.claimID}
                            customer={cust}
                            onFullReport={setScriptReport}
                            onCommentSaved={handleCommentSaved}
                            onRefresh={loadData}
                            onOpenCompose={openCompose}
                            onEmailSuccess={(msg) => showToast(msg)}
                            onEmailError={(msg) => showToast(msg, "error")}
                            showUnseen={showUnseen}
                            insCompEmail={selectedGroup?.insCompEmail ?? ""}
                          />
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          )}
        </>
      )}

      <FullScriptReportModal
        open={!!scriptReport}
        claimID={scriptReport}
        onClose={() => setScriptReport(null)}
      />

      <ZephyrComposeModal
        open={!!composeModal}
        onClose={() => setComposeModal(null)}
        invoiceID={composeModal?.invoiceID ?? null}
        claimID={composeModal?.claimID ?? null}
        insurerEmail={composeModal?.insCompEmail ?? selectedGroup?.insCompEmail ?? ""}
        customerName={composeModal?.customerName ?? ""}
        claimNumber={composeModal?.claimNumber ?? ""}
        onSuccess={(msg) => { showToast(msg); setComposeModal(null); }}
        onError={(msg) => showToast(msg, "error")}
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={4000}
        onClose={hideToast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert severity={toast?.severity ?? "success"} onClose={hideToast} sx={{ width: "100%" }}>
          {toast?.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
}
